import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosDelete, axiosGet, axiosPatch, axiosPost } from 'Redux/axiosHelper'
import {
  AgreementParamsType,
  AgreementPayloadType,
  AgreementResp,
} from 'types/agreement/agreement'

const addAgreement = async (payload: AgreementPayloadType) => {
  const response = await axiosPost(payload, `v1/agreement`)
  return response.data
}

export const useAddAgreement = () => {
  const queryClient = useQueryClient()
  return useMutation(addAgreement, {
    onSuccess: data => {
      queryClient.setQueryData(
        ['get-agreements-by-id'],
        (oldData: AgreementResp[] | undefined) => {
          if (!oldData) return [data]
          return [data, ...oldData]
        },
      )
    },
  })
}

// get all agreements
const getAgreements = async (params: AgreementParamsType) => {
  const response = await axiosGet(`v1/agreement`, params)
  return response.data
}

export const useGetAgreements = (params: AgreementParamsType) => {
  const allAgreements = useQuery<AgreementResp[]>(
    ['get-agreements-by-id'],
    () => getAgreements(params),
    { enabled: false },
  )
  return allAgreements
}

// update agreement
const updateAgreement = async ({
  payload,
  agreementId,
}: {
  payload: { agreement: string }
  agreementId: string
}) => {
  const response = await axiosPatch(payload, `v1/agreement/${agreementId}`)
  return response.data
}

export const useUpdateAgreement = () => {
  const queryClient = useQueryClient()
  return useMutation(updateAgreement, {
    onSuccess: updatedData => {
      queryClient.setQueryData(
        ['get-agreements-by-id'],
        (oldData: AgreementResp[] | undefined) => {
          if (!oldData) return [updatedData]
          return oldData.map(agreement =>
            agreement._id === updatedData._id ? updatedData : agreement,
          )
        },
      )
      queryClient.setQueryData(
        ['get-app-agreements'],
        (oldData: AgreementResp[] | undefined) => {
          if (!oldData) return [updatedData]
          return oldData.map(agreement =>
            agreement._id === updatedData._id ? updatedData : agreement,
          )
        },
      )
    },
  })
}

// delete agreement
const deleteAgreement = async (agreementId: string) => {
  const response = await axiosDelete(`v1/agreement/${agreementId}`)
  return response.data
}

export const useDeleteAgreement = () => {
  const queryClient = useQueryClient()
  return useMutation(deleteAgreement, {
    onSuccess: (deletedAgreement: AgreementResp) => {
      queryClient.setQueryData(
        ['get-agreements-by-id'],
        (oldData: AgreementResp[] | undefined) => {
          if (!oldData) return []
          return oldData.filter(
            agreement => agreement._id !== deletedAgreement._id,
          )
        },
      )
    },
  })
}

// get app agreements
const getAppAgreements = async (params: {
  forApp: 'guardian' | 'mentor' | 'organizer'
}) => {
  const response = await axiosGet(`v1/agreement/app-agreements`, params)
  return response.data
}

export const useGetAppAgreements = (params: {
  forApp: 'guardian' | 'mentor' | 'organizer'
}) => {
  const allAgreements = useQuery<AgreementResp[]>(
    ['get-app-agreements'],
    () => getAppAgreements(params),
    { enabled: false },
  )
  return allAgreements
}
